<template>
    <div class="card notranslate" v-bind:class="viewGa">
        <div class="card_header">
            <SboIcon
                name="system:fire"
                class="hotIcon"
                size="16"
            />
            <span class="title">{{ getTranslationBySite('mpCombination_popular_mix_parlay') }}</span>
        </div>
        <div class="card_content">
            <ul>
                <MpCombinationTicket
                    v-for="ticket in visibleTickets"
                    v-bind:key="ticket.oddsId + '-' + ticket.option"
                    v-bind:ticket="ticket"
                />
            </ul>
        </div>
        <div class="card_footer">
            <dl class="textInfo">
                <dt>
                    {{ i18n.t('mpCombination_max_Odds') }}:
                </dt>
                <dd v-bind:class="{ 'odds-change': oddsChanging }">
                    @{{ maxOdds }}
                </dd>
            </dl>
            <Cta
                customClass="btn btn-accent"
                v-bind:ga="addToSlipGa"
                v-bind:onClick="addToBetSlip"
            >
                <strong>{{ i18n.t('mpCombination_add_to_slip') }}</strong>
            </Cta>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { computed, onUnmounted, ref, watch } from 'vue';
    import Cta from '@/components/common/Cta.vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import MpCombinationTicket from '@/components/recommendations/MpCombinationTicket.vue';
    import type { IMpResolvedCombination, IMpResolvedTicket } from '@/interface/IPopularMixParlay';
    import { BetPageType, DisplaySection, SportType } from '@/interface/enum';
    import { createEuroTicket } from '@/libraries/euroTicket';
    import { getTotalOdds } from '@/libraries/helpers/comboParlayHelper';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { i18n } from '@/libraries/i18n';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useParlayBetSlipStore } from '@/stores/betSlip/parlayBetSlipStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useRootStore } from '@/stores/rootStore';

    interface Props {
        combination: IMpResolvedCombination;
        order: number;
    }

    const props = defineProps<Props>();

    const visibleTickets = computed<IMpResolvedTicket[]>(() => props.combination.tickets
        .filter(ticket => ticket.event && ticket.odds && ticket.priceOption));

    const { showMediumBetSlip } = useBetSlipStore();
    const { betSlip } = storeToRefs(useParlayBetSlipStore());
    const { removeTicket, addTicket, setOpenFrom } = useParlayBetSlipStore();

    const { isEuroSite } = storeToRefs(useFeatureToggleStore());

    const addToBetSlip = () => {
        Object.keys(betSlip.value.tickets).forEach((key) => {
            removeTicket(key);
        });
        visibleTickets.value.forEach((ticket) => {
            if (!ticket.event || !ticket.odds || !ticket.priceOption) return;

            const betSlipTicket = createEuroTicket(ticket.event, ticket.odds, ticket.priceOption, BetPageType.MpCombination, true);
            addTicket(betSlipTicket);
        });
        if (!isEuroSite.value) {
            showMediumBetSlip();
        }
        setOpenFrom(DisplaySection.MpCombination);
    };

    const maxOdds = computed(() => {
        if (!props.combination.isResolved) return '--';
        const selectedOptions = visibleTickets.value.map(ticket => ticket.priceOption);
        return getTotalOdds(selectedOptions.map(x => x?.price || 1));
    });

    const { sportType } = storeToRefs(useRootStore());
    const viewGa = computed(() => `MP_Combination_${SportType[sportType.value]}_${props.order}_View`);
    const addToSlipGa = computed(() => `MP_Combination_${SportType[sportType.value]}_${props.order}_Btn_Add`);

    const oddsChanging = ref(false);
    let timeoutId: number;

    watch(maxOdds, (newVal, oldVal) => {
        if (newVal !== oldVal) {
            oddsChanging.value = true;

            clearTimeout(timeoutId);
            timeoutId = window.setTimeout(() => {
                oddsChanging.value = false;
            }, 3000);
        }
    });

    onUnmounted(() => {
        clearTimeout(timeoutId);
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/quickCombination";
    @import "@/scss/components/quickCombinationTicket";
</style>
